import apiClient from './apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ==========================================================================
// Network Print Bridge API
// Sends TSPL labels to a USB-connected TSC printer on a remote Windows PC
// via the backend job queue (relay/poll model). See routes/print_bridge.py.
// ==========================================================================

// List all printers reported by registered agents (+ this user's default).
export const listNetworkPrinters = async () => {
  const res = await apiClient.get('/print-bridge/printers');
  return res.data; // { printers: [...], default_pref }
};

export const submitPrintJob = async ({ agentId, printerName, payload, copies = 1, labelType = null, meta = null, operationKey = null, clientToken = null }) => {
  const res = await apiClient.post('/print-bridge/jobs', {
    agent_id: agentId,
    printer_name: printerName,
    payload,
    copies,
    label_type: labelType,
    meta,
    operation_key: operationKey,
    client_token: clientToken,
  });
  return res.data; // { job_id, status, agent_online, routed_via_operation }
};

export const getPrintJob = async (jobId) => {
  const res = await apiClient.get(`/print-bridge/jobs/${jobId}`);
  return res.data;
};

// The station centrally assigned to an operation (if any).
export const getOperationRoute = async (operationKey) => {
  const res = await apiClient.get(`/print-bridge/operation-route/${encodeURIComponent(operationKey)}`);
  return res.data; // { assigned, route }
};

export const setPrintPreference = async ({ agentId, printerName }) => {
  const res = await apiClient.put('/print-bridge/preferences', {
    agent_id: agentId,
    printer_name: printerName,
  });
  return res.data;
};

// Resolve the desired {agentId, printerName} to a VALID, ONLINE station.
// Stations can be deleted & recreated (new agent_id) or go offline, leaving a
// stale saved selection that would otherwise enqueue a job nothing ever prints
// (30s timeout). We re-point to an online station advertising the same physical
// printer whenever possible, so printing "just works" after a station reset.
const resolveOnlineTarget = async ({ agentId, printerName }) => {
  let printers = [];
  try {
    const data = await listNetworkPrinters();
    printers = data?.printers || [];
  } catch (_) {
    return { agentId }; // listing failed — fall back to the saved target
  }
  const online = printers.filter((p) => p.online);
  const exact = online.find((p) => p.agent_id === agentId && p.printer_name === printerName);
  if (exact) return { agentId };

  const sameName = online.filter((p) => p.printer_name === printerName);
  if (sameName.length === 1) {
    // Auto-heal: the saved station is gone/offline but exactly one online
    // station has this printer — use it and remember it as the new default.
    const healed = sameName[0].agent_id;
    setPrintPreference({ agentId: healed, printerName }).catch(() => {});
    // Also correct the active in-browser target so we don't keep carrying the
    // stale agent_id for the rest of the session.
    try {
      localStorage.setItem('lp_active_printer', JSON.stringify({
        agentId: healed, printerName, agentName: sameName[0].agent_name,
      }));
    } catch (_) { /* ignore */ }
    return { agentId: healed, healed: true };
  }
  if (sameName.length > 1) {
    return { error: `More than one online station has "${printerName}". Tap the printer name to choose one.`, needsPicker: true };
  }
  if (online.length === 0) {
    return { error: 'No print station is online. Start the Print Bridge app on the printer PC, then try again.', needsPicker: true };
  }
  return { error: `Printer "${printerName}" isn't available on any online station. Tap the printer name to pick an online one.`, needsPicker: true };
};

// Submit a job then poll until it finishes (or times out).
export const printViaAgent = async (
  { agentId, printerName, payload, copies = 1, labelType, meta, operationKey = null, clientToken = null },
  { timeoutMs = 60000, intervalMs = 1500 } = {},
) => {
  let targetAgentId = agentId;
  let targetPrinter = printerName;

  // If this operation is centrally routed, that station wins — use it (and
  // surface a clear error if it's missing/offline). Otherwise fall back to the
  // saved selection with printer-name auto-heal.
  let routeHandled = false;
  if (operationKey) {
    try {
      const { assigned, route } = await getOperationRoute(operationKey);
      if (assigned && route) {
        if (!route.exists) {
          return { success: false, error: 'The station assigned to this operation no longer exists. Ask an admin to re-assign it in Print Stations → Operation Routing.', needsPicker: false };
        }
        if (!route.online) {
          return { success: false, error: `The station assigned to this operation ("${route.agent_name}") is offline. Start its Print Bridge app, or ask an admin to re-assign it.`, needsPicker: false };
        }
        targetAgentId = route.agent_id;
        targetPrinter = route.printer_name;
        routeHandled = true;
      }
    } catch (_) { /* route lookup failed — backend still routes by operation_key */ }
  }

  if (!routeHandled) {
    const resolved = await resolveOnlineTarget({ agentId, printerName });
    if (resolved.error) return { success: false, error: resolved.error, needsPicker: resolved.needsPicker };
    targetAgentId = resolved.agentId;
  }

  const submit = await submitPrintJob({ agentId: targetAgentId, printerName: targetPrinter, payload, copies, labelType, meta, operationKey, clientToken });
  const jobId = submit.job_id;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const j = await getPrintJob(jobId);
    if (j.status === 'printed') return { success: true, jobId };
    if (j.status === 'failed') return { success: false, jobId, error: j.error || 'Print failed at the station' };
    if (j.status === 'cancelled') return { success: false, jobId, error: 'Print job was cancelled' };
  }
  return {
    success: false,
    jobId,
    error: 'Still waiting for the print station to confirm — the label may already have printed. Check the printer before retrying; a retry will not print a duplicate.',
    timedOut: true,
  };
};

// ==========================================================================
// Admin — Print Station management
// ==========================================================================
export const adminListOperationRoutes = async () => {
  const res = await apiClient.get('/print-bridge/admin/operation-routes');
  return res.data; // { operations: [...] }
};

export const adminSetOperationRoute = async (operationKey, { agentId, printerName } = {}) => {
  const res = await apiClient.put(`/print-bridge/admin/operation-routes/${encodeURIComponent(operationKey)}`, {
    agent_id: agentId || null,
    printer_name: printerName || null,
  });
  return res.data;
};

export const adminListAgents = async () => {
  const res = await apiClient.get('/print-bridge/admin/agents');
  return res.data; // { agents: [...] }
};

export const adminCreateAgent = async (name) => {
  const res = await apiClient.post('/print-bridge/admin/agents', { name });
  return res.data; // { agent }
};

export const adminRotateKey = async (agentId) => {
  const res = await apiClient.post(`/print-bridge/admin/agents/${agentId}/rotate-key`);
  return res.data; // { agent }
};

export const adminDeleteAgent = async (agentId) => {
  const res = await apiClient.delete(`/print-bridge/admin/agents/${agentId}`);
  return res.data;
};

export const adminTestPrint = async (agentId, printerName = null) => {
  const res = await apiClient.post(`/print-bridge/admin/agents/${agentId}/test-print`, { printer_name: printerName });
  return res.data;
};

export const adminListJobs = async ({ agentId, status, fromDate, toDate, skip = 0, limit = 50 } = {}) => {
  const params = { skip, limit };
  if (agentId) params.agent_id = agentId;
  if (status) params.status = status;
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  const res = await apiClient.get('/print-bridge/admin/jobs', { params });
  return res.data;
};

export const adminReprintJob = async (jobId) => {
  const res = await apiClient.post(`/print-bridge/admin/jobs/${jobId}/reprint`);
  return res.data;
};

// Download the pre-configured Windows agent zip for a station.
export const adminDownloadAgent = async (agentId, stationName = 'station') => {
  const backendUrl = BACKEND_URL || window.location.origin;
  const res = await apiClient.get(`/print-bridge/admin/agents/${agentId}/download`, {
    params: { backend_url: backendUrl },
    responseType: 'blob',
  });
  const safe = (stationName || 'station').replace(/[^a-zA-Z0-9-_]/g, '') || 'station';
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `print-bridge-agent-${safe}.zip`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return { success: true };
};
